import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button } from '@mui/material';

// Shared styled-components + scroll-story behavior for the /demo marketing pages
// (MarketingLandingPageV3 and the solo feature pages under src/pages/marketing/).

export const NavBar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 48px;
  z-index: 1000;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    padding: 0 24px;
  }
`;

export const Logo = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #e94560;
  cursor: pointer;

  &::before {
    content: 'Draft Planner';
  }
`;

export const LoginButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 10px 32px;
    border-radius: 24px;
    font-weight: 600;
    text-transform: none;
  }
`;

export const PageContainer = styled.div`
  width: 100%;
  background: #121212;
  scroll-behavior: smooth;
  scroll-snap-type: ${props => props.$disableSnap ? 'none' : 'y mandatory'};
  height: 100vh;
  overflow-y: scroll;
`;

export const Section = styled.section`
  height: 100vh;
  display: flex;
  flex-direction: ${props => props.$reverse ? 'row-reverse' : 'row'};
  align-items: center;
  justify-content: space-between;
  padding: 80px 80px;
  scroll-snap-align: start;
  position: relative;
  gap: 60px;
  background: #121212;
  overflow: hidden;

  @media (max-width: 968px) {
    flex-direction: column;
    padding: 100px 24px;
    gap: 32px;
  }
`;

export const ContentColumn = styled.div`
  flex: 0 0 40%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 10;
  opacity: 1;
  transform: translateY(0);

  &[data-animate="content"] {
    opacity: 0;
    transform: translateY(50px);
    transition: opacity 0.8s ease-out, transform 0.8s ease-out;

    &.visible {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 968px) {
    flex: 1;
    width: 100%;
  }
`;

export const DemoColumn = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  opacity: 1;
  transform: translateY(0);

  &[data-animate="demo"] {
    opacity: 0;
    transform: translateY(50px);
    transition: opacity 0.8s ease-out 0.3s, transform 0.8s ease-out 0.3s;

    &.visible {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 968px) {
    width: 100%;
  }
`;

export const FeatureTitle = styled.h2`
  font-size: 56px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 24px 0;
  text-align: left;

  @media (max-width: 968px) {
    font-size: 36px;
    text-align: center;
  }
`;

export const FeatureDescription = styled.p`
  font-size: 22px;
  color: rgba(255, 255, 255, 0.8);
  text-align: left;
  margin: 0;
  line-height: 1.6;

  @media (max-width: 968px) {
    font-size: 18px;
    text-align: center;
  }
`;

export const Highlight = styled.span`
  color: #667eea;
  font-weight: 700;
`;

export const OriginStory = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  text-align: left;
  margin: 0 0 16px 0;
  line-height: 1.5;
  border-left: 3px solid rgba(102, 126, 234, 0.5);
  padding-left: 16px;

  @media (max-width: 968px) {
    font-size: 16px;
    text-align: center;
    border-left: none;
    border-top: 3px solid rgba(102, 126, 234, 0.5);
    padding-left: 0;
    padding-top: 12px;
  }
`;

// Full-screen narration section (no demo, just story + visual)
export const NarrationSection = styled.section`
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px;
  scroll-snap-align: start;
  position: relative;
  background: #121212;
  overflow: hidden;

  /* Narration sections are always visible */
  opacity: 1;

  @media (max-width: 968px) {
    padding: 60px 24px;
  }
`;

export const NarrationQuote = styled.h2`
  font-size: 48px;
  font-weight: 700;
  font-style: italic;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  max-width: 900px;
  line-height: 1.4;
  margin: 0;
  position: relative;
  z-index: 10;

  &::before {
    content: '"';
    font-size: 120px;
    position: absolute;
    top: -40px;
    left: -60px;
    color: rgba(102, 126, 234, 0.2);
    font-family: Georgia, serif;
  }

  @media (max-width: 968px) {
    font-size: 28px;

    &::before {
      font-size: 80px;
      top: -30px;
      left: -30px;
    }
  }
`;

export const VisualIcon = styled.div`
  width: 180px;
  height: 180px;
  margin-bottom: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 0 20px rgba(102, 126, 234, 0.5));
  }

  @media (max-width: 968px) {
    width: 120px;
    height: 120px;
    margin-bottom: 32px;
  }
`;

export const DemoContainer = styled.div`
  display: flex;
  gap: 24px;
  max-width: 100%;
  width: 100%;
  justify-content: center;
  /* Real board renders ~1150-1250px tall (24 pick slots); shrink it to fit inside
     the 100vh section instead of letting it clip top/bottom. */
  zoom: 0.65;

  @media (max-width: 968px) {
    flex-direction: column;
    align-items: center;
    zoom: 0.55;
  }
`;

export const CTASection = styled(Section)`
  background: linear-gradient(180deg, #121212 0%, #0a0a0a 100%);
  flex-direction: column;
  text-align: center;
`;

export const HeroSection = styled(Section)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  /* Hero section is always visible (no animation) */
  ${ContentColumn}, ${FeatureTitle}, ${OriginStory}, ${FeatureDescription} {
    opacity: 1 !important;
    transform: none !important;
  }
`;

export const CTAButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #e94560 0%, #ff6b6b 100%);
    color: white;
    padding: 20px 60px;
    border-radius: 32px;
    font-weight: 700;
    font-size: 24px;
    text-transform: none;
    box-shadow: 0 8px 32px rgba(233, 69, 96, 0.4);

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 16px 48px rgba(233, 69, 96, 0.6);
    }
  }
`;

/**
 * Shared scroll-story behavior for the /demo marketing pages:
 * - IntersectionObserver reveal animation for [data-animate] elements inside [data-section-id] sections
 * - Disables scroll-snap while arrow keys are used (so DraftBoard keyboard nav isn't fought by the page scroll)
 *
 * `firstSectionId` is the data-section-id whose content should animate in immediately on mount
 * (since it may already be in the viewport before the IntersectionObserver's first tick).
 */
export const useScrollStory = (firstSectionId) => {
  const [disableSnap, setDisableSnap] = useState(false);
  const [visibleSection, setVisibleSection] = useState(null);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleSection(entry.target.dataset.sectionId);

          const contentColumn = entry.target.querySelector('[data-animate="content"]');
          const demoColumn = entry.target.querySelector('[data-animate="demo"]');

          if (contentColumn) contentColumn.classList.add('visible');
          if (demoColumn) demoColumn.classList.add('visible');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const sections = document.querySelectorAll('[data-section-id]');
    sections.forEach((section) => observer.observe(section));

    if (firstSectionId) {
      setTimeout(() => {
        const firstSection = document.querySelector(`[data-section-id="${firstSectionId}"]`);
        if (firstSection) {
          const rect = firstSection.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            const contentColumn = firstSection.querySelector('[data-animate="content"]');
            const demoColumn = firstSection.querySelector('[data-animate="demo"]');
            if (contentColumn) contentColumn.classList.add('visible');
            if (demoColumn) demoColumn.classList.add('visible');
          }
        }
      }, 100);
    }

    return () => observer.disconnect();
  }, [firstSectionId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        setDisableSnap(true);
      }
    };

    const handleKeyUp = (e) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        setTimeout(() => setDisableSnap(false), 1000);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  return { disableSnap, visibleSection };
};
